
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Search as SearchIcon, 
  MapPin, 
  ChevronRight, 
  BrainCircuit, 
  ShieldCheck, 
  Building2, 
  ArrowRight,
  Target,
  Users,
  Briefcase,
  Star,
  CheckCircle2,
  Check,
  Zap,
  Shield,
  SearchCheck,
  UserCheck,
  RefreshCw,
  Hammer
} from 'lucide-react';
import { CATEGORIES, PRICING_PLANS } from '../constants';
import { listingService } from '../services/listingService';
import { Professional, Category, ListingTier } from '../types';
import ListingCard from '../components/ListingCard';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [eliteSponsors, setEliteSponsors] = useState<Professional[]>([]);
  const [visibleElites, setVisibleElites] = useState<Professional[]>([]);
  const [rotationIndex, setRotationIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  // 1. Load All Professionals and Filter Elites
  useEffect(() => {
    const loadData = async () => {
      const all = await listingService.getAllListings();
      
      // Filter for EXCLUSIVE tier listings (Elite Sponsors)
      const elites = all.filter(p => p.tier === ListingTier.EXCLUSIVE);
      const premiums = all.filter(p => p.tier === ListingTier.PREMIUM);
      
      // If we have few elites, mix in premiums as fallback so the grid is never empty
      const combined = elites.length >= 2 ? elites : [...elites, ...premiums].slice(0, 4);
      
      setEliteSponsors(combined);
      setLoading(false);
    };
    loadData();
    document.title = "PlanStack | Australia's Premium Planning Directory";
  }, []);

  // 2. Rotation Logic: Cycle visible listings every 5 seconds
  useEffect(() => {
    if (eliteSponsors.length <= 2) {
      setVisibleElites(eliteSponsors);
      return;
    }

    const rotate = () => {
      setRotationIndex((prev) => (prev + 1) % eliteSponsors.length);
    };

    const interval = setInterval(rotate, 5000);
    
    // Calculate which 2 listings to show based on rotation index
    const firstIdx = rotationIndex;
    const secondIdx = (rotationIndex + 1) % eliteSponsors.length;
    setVisibleElites([eliteSponsors[firstIdx], eliteSponsors[secondIdx]]);

    return () => clearInterval(interval);
  }, [eliteSponsors, rotationIndex]);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* 1. HERO SECTION */}
      <section className="relative pt-24 pb-32 overflow-hidden bg-brand-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-500 rounded-full blur-[120px] -mr-96 -mt-96"></div>
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-500 rounded-full blur-[120px] -ml-48 -mb-48"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-md px-4 py-2 rounded-full border border-white/10 text-brand-100 text-[11px] font-bold uppercase tracking-[0.2em] mb-8 animate-fade-in">
              <ShieldCheck size={14} className="text-blue-400" /> Vetted Professionals • National Network
            </div>
            <h1 className="text-6xl md:text-8xl font-black text-white leading-[0.9] tracking-tight mb-8 animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Certainty for <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-300">Your Development</span>
            </h1>
            <p className="text-xl md:text-2xl text-brand-100/70 font-medium mb-12 max-w-2xl leading-relaxed animate-fade-in" style={{ animationDelay: '0.2s' }}>
              Find and Connect with Australia's elite Builders, Town Planners, and Architects. 
              Reduce risk and accelerate approvals with vetted local expertise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <div className="relative flex-grow max-w-xl">
                 <SearchIcon className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                 <input 
                    type="text" 
                    placeholder="Search by practice name or suburb..."
                    className="w-full bg-white rounded-2xl py-6 pl-16 pr-6 text-slate-900 text-lg font-medium outline-none focus:ring-4 focus:ring-blue-500/20 shadow-2xl transition-all"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(`/directory?q=${searchTerm}`)}
                 />
                 <button 
                  onClick={() => navigate(`/directory?q=${searchTerm}`)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-brand-900 text-white px-8 py-3.5 rounded-xl font-bold text-sm uppercase tracking-widest hover:bg-slate-800 transition"
                 >
                  Search
                 </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. INDUSTRY PILLARS */}
      <section className="py-24 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tight mb-4">Core Disciplines</h2>
            <div className="w-20 h-1 bg-blue-600 mx-auto"></div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { name: 'Builders', icon: <Hammer />, count: '310+', color: 'bg-orange-600', category: Category.BUILDER },
              { name: 'Architects', icon: <Building2 />, count: '240+', color: 'bg-blue-500', category: Category.ARCHITECTURE },
              { name: 'Town Planners', icon: <Target />, count: '180+', color: 'bg-indigo-600', category: Category.TOWN_PLANNING },
              { name: 'Surveyors', icon: <MapPin />, count: '120+', color: 'bg-slate-900', category: Category.LAND_SURVEYING },
              { name: 'Engineers', icon: <Users />, count: '150+', color: 'bg-blue-900', category: Category.ENGINEERING_CIVIL },
            ].map((pillar) => (
              <Link 
                key={pillar.name}
                to={`/directory?category=${pillar.category}`}
                className="group bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-500"
              >
                <div className={`${pillar.color} w-12 h-12 rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition duration-500 shadow-lg`}>
                   {pillar.icon}
                </div>
                <h3 className="text-lg font-black text-slate-900 mb-1">{pillar.name}</h3>
                <p className="text-slate-400 text-sm font-bold uppercase tracking-widest">{pillar.count} Specialists</p>
                <div className="mt-6 flex items-center text-blue-600 text-[10px] font-black uppercase tracking-widest opacity-0 group-hover:opacity-100 transition">
                  Explore Registry <ArrowRight size={14} className="ml-2" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 3. ROTATING ELITE SPOTLIGHT */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div className="max-w-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-1.5 bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border border-blue-100 shadow-sm">
                   <RefreshCw size={10} className="animate-spin" /> Live Spotlight
                </div>
                <span className="text-slate-300 font-bold text-[9px] uppercase tracking-[0.2em]">Rotating Elite Partners</span>
              </div>
              <h2 className="text-4xl font-black text-slate-900 leading-none uppercase tracking-tight mb-6">Market Leaders</h2>
              <p className="text-slate-500 font-medium">Verified practices demonstrating excellence across Australian planning jurisdictions.</p>
            </div>
            <Link to="/directory" className="text-blue-600 font-black uppercase tracking-widest text-[11px] flex items-center gap-2 hover:translate-x-1 transition">
              View All 1,200+ Practices <ChevronRight size={16}/>
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 gap-8">
               {[1,2].map(i => <div key={i} className="h-64 bg-slate-50 animate-pulse rounded-3xl"></div>)}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {visibleElites.map((pro, idx) => (
                <div 
                  key={`${pro.id}-${rotationIndex}`} 
                  className="animate-fade-in"
                  style={{ animationDuration: '0.8s' }}
                >
                  <ListingCard professional={pro} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* 4. AI PROJECT WIZARD CTA */}
      <section className="py-24 overflow-hidden relative">
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-slate-900 rounded-[3rem] p-12 md:p-24 flex flex-col lg:flex-row items-center justify-between gap-16 relative shadow-2xl overflow-hidden border border-slate-800">
               <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[100px] -mr-48 -mt-48"></div>
               <div className="max-w-xl relative z-10">
                  <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.2em] mb-8">
                     <BrainCircuit size={16} /> Intelligence Layer
                  </div>
                  <h2 className="text-4xl md:text-6xl font-black text-white mb-8 leading-tight tracking-tight">
                    Build Your <br/>Project Team <br/>with AI.
                  </h2>
                  <p className="text-lg text-slate-400 font-medium leading-relaxed mb-12">
                    Enter your property address and project brief. Our Planning Engine will identify the 
                    consultants and reports you likely need to secure a permit.
                  </p>
                  <Link to="/wizard" className="inline-flex items-center gap-3 bg-blue-600 hover:bg-blue-500 text-white font-black px-10 py-5 rounded-2xl shadow-xl transition-all hover:scale-105 active:scale-95 uppercase tracking-widest text-xs">
                    Launch Project Wizard <ArrowRight size={18} />
                  </Link>
               </div>
               <div className="w-full lg:w-1/3 relative z-10 flex flex-col gap-6">
                  <div className="bg-white/5 border border-white/10 p-8 rounded-3xl backdrop-blur-xl translate-x-12 hover:translate-x-6 transition duration-500">
                     <div className="flex items-center gap-4 mb-4">
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center font-bold text-white">1</div>
                        <h4 className="font-bold text-white">Site Analysis</h4>
                     </div>
                     <p className="text-slate-400 text-sm">Automated review of planning zones and overlays.</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 p-8 rounded-3xl backdrop-blur-xl -translate-x-12 hover:-translate-x-6 transition duration-500">
                     <div className="flex items-center gap-4 mb-4">
                        <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center font-bold text-white">2</div>
                        <h4 className="font-bold text-white">Team Builder</h4>
                     </div>
                     <p className="text-slate-400 text-sm">Curated list of specialists required for your proposal.</p>
                  </div>
               </div>
            </div>
         </div>
      </section>

      {/* 5. PARTNER PROGRAMS (PRICING) */}
      <section className="py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-xs font-black text-blue-600 uppercase tracking-[0.3em] mb-4">Professional Access</h2>
            <h3 className="text-4xl md:text-6xl font-black text-slate-900 uppercase tracking-tight mb-6">Partner With Us</h3>
            <p className="text-slate-500 font-medium max-w-2xl mx-auto">List your practice where property developers search for expertise. Secure your region today.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {PRICING_PLANS.map((plan, index) => (
              <div 
                key={index}
                className={`
                  relative bg-white rounded-[2.5rem] p-8 flex flex-col border transition-all duration-500
                  ${plan.highlight ? 'ring-4 ring-blue-500 border-transparent shadow-2xl scale-105 z-10' : 'border-slate-200 shadow-sm hover:shadow-xl'}
                `}
              >
                {plan.highlight && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-500 text-white px-6 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg">
                    Recommended
                  </div>
                )}
                <div className="mb-8">
                  <h4 className="text-lg font-black text-slate-900 mb-2">{plan.name}</h4>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-black text-slate-900">{plan.price}</span>
                    {plan.price !== 'Free' && <span className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">/mo</span>}
                  </div>
                </div>
                <ul className="space-y-4 mb-10 flex-1">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-xs font-medium text-slate-600">
                      <Check size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link 
                  to={plan.price === 'Free' ? "/signup" : "/join"}
                  className={`
                  w-full py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest text-center transition shadow-lg
                  ${plan.highlight ? 'bg-slate-900 text-white hover:bg-black' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}
                `}>
                  Select Program
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
